import { Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppShell } from './app/AppShell';
import { NotFoundPage } from './app/NotFoundPage';
import { lazyRoute } from './app/routeProgress';
import { Skeleton } from './components/ui/skeleton';
import { AcceptInvitePage } from './features/auth/AcceptInvitePage';
import { AuthProvider } from './features/auth/AuthContext';
import { LoginPage } from './features/auth/LoginPage';
import { RequireAuth } from './features/auth/RequireAuth';
import { RequireWorkspace } from './features/tenants/RequireWorkspace';

// Feature screens are split per route: the login page and shell load first, and
// a section's code only arrives when someone navigates to it. Without this the
// single bundle grows with every screen added. `lazyRoute` (not React's plain
// `lazy`) also reports into RouteProgressBar.tsx's loading counter.
const OverviewPage = lazyRoute(() =>
  import('./features/overview/OverviewPage').then((m) => ({ default: m.OverviewPage })),
);
const BotsPage = lazyRoute(() =>
  import('./features/bots/BotsPage').then((m) => ({ default: m.BotsPage })),
);
const ToolsPage = lazyRoute(() =>
  import('./features/tools/ToolsPage').then((m) => ({ default: m.ToolsPage })),
);
const SourcesPage = lazyRoute(() =>
  import('./features/sources/SourcesPage').then((m) => ({ default: m.SourcesPage })),
);
const ChatsPage = lazyRoute(() =>
  import('./features/chats/ChatsPage').then((m) => ({ default: m.ChatsPage })),
);
const TeamPage = lazyRoute(() =>
  import('./features/users/TeamPage').then((m) => ({ default: m.TeamPage })),
);
const AdminsPage = lazyRoute(() =>
  import('./features/users/AdminsPage').then((m) => ({ default: m.AdminsPage })),
);
const BlueprintsPage = lazyRoute(() =>
  import('./features/blueprints/BlueprintsPage').then((m) => ({ default: m.BlueprintsPage })),
);
const UsagePage = lazyRoute(() =>
  import('./features/usage/UsagePage').then((m) => ({ default: m.UsagePage })),
);
const TokensPage = lazyRoute(() =>
  import('./features/tokens/TokensPage').then((m) => ({ default: m.TokensPage })),
);
const SettingsPage = lazyRoute(() =>
  import('./features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const TenantsPage = lazyRoute(() =>
  import('./features/tenants/TenantsPage').then((m) => ({ default: m.TenantsPage })),
);
const AuditLogPage = lazyRoute(() =>
  import('./features/audit/AuditLogPage').then((m) => ({ default: m.AuditLogPage })),
);

function RouteFallback() {
  return (
    <div className="grid gap-4">
      <Skeleton className="h-9 w-56" />
      <Skeleton className="h-40" />
    </div>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/accept-invite" element={<AcceptInvitePage />} />

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          {/* Tenant-scoped pages (mirrors `require_active_tenant` on the
              backend, plus Team, which is scoped to the active tenant even
              though the backend doesn't gate it) render an inline "no active
              workspace" prompt instead of their content when session.tenant
              is null, rather than blocking the whole app — Admins,
              Workspaces, and Settings stay usable either way, since
              OWNER/ADMIN must be able to get in and add staff before any
              workspace exists. */}
          <Route
            index
            element={
              <RequireWorkspace>
                <Suspense fallback={<RouteFallback />}>
                  <OverviewPage />
                </Suspense>
              </RequireWorkspace>
            }
          />

          <Route
            path="bots"
            element={
              <RequireAuth permission="bots:read">
                <RequireWorkspace>
                  <Suspense fallback={<RouteFallback />}>
                    <BotsPage />
                  </Suspense>
                </RequireWorkspace>
              </RequireAuth>
            }
          />
          <Route
            path="tools"
            element={
              <RequireAuth permission="tools:read">
                <RequireWorkspace>
                  <Suspense fallback={<RouteFallback />}>
                    <ToolsPage />
                  </Suspense>
                </RequireWorkspace>
              </RequireAuth>
            }
          />
          <Route
            path="sources"
            element={
              <RequireAuth permission="sources:read">
                <RequireWorkspace>
                  <Suspense fallback={<RouteFallback />}>
                    <SourcesPage />
                  </Suspense>
                </RequireWorkspace>
              </RequireAuth>
            }
          />
          <Route
            path="chats"
            element={
              <RequireAuth permission="chats:read">
                <RequireWorkspace>
                  <Suspense fallback={<RouteFallback />}>
                    <ChatsPage />
                  </Suspense>
                </RequireWorkspace>
              </RequireAuth>
            }
          />
          {/* Same screen, with a conversation selected — so a transcript can be
              linked to and survives a refresh. */}
          <Route
            path="chats/:chatId"
            element={
              <RequireAuth permission="chats:read">
                <RequireWorkspace>
                  <Suspense fallback={<RouteFallback />}>
                    <ChatsPage />
                  </Suspense>
                </RequireWorkspace>
              </RequireAuth>
            }
          />
          <Route
            path="usage"
            element={
              <RequireAuth permission="stats:read">
                <RequireWorkspace>
                  <Suspense fallback={<RouteFallback />}>
                    <UsagePage />
                  </Suspense>
                </RequireWorkspace>
              </RequireAuth>
            }
          />
          <Route
            path="tokens"
            element={
              <RequireAuth permission="tokens:read">
                <RequireWorkspace>
                  <Suspense fallback={<RouteFallback />}>
                    <TokensPage />
                  </Suspense>
                </RequireWorkspace>
              </RequireAuth>
            }
          />
          {/* Not wrapped in RequireWorkspace: an OWNER/ADMIN must be able to
              reach Team and add staff before any workspace exists at all —
              see RequireWorkspace.tsx's own docstring, which already
              documented this as the intent. TeamPage itself renders
              correctly with session.tenant null (its business-role list is
              simply empty until a workspace exists). */}
          <Route
            path="users"
            element={
              <RequireAuth permission="users:read">
                <Suspense fallback={<RouteFallback />}>
                  <TeamPage />
                </Suspense>
              </RequireAuth>
            }
          />

          {/* System-level pages — nothing here belongs to any one tenant, so
              none of them are wrapped in RequireWorkspace. */}
          <Route
            path="admins"
            element={
              <RequireAuth permission="users:manage_staff">
                <Suspense fallback={<RouteFallback />}>
                  <AdminsPage />
                </Suspense>
              </RequireAuth>
            }
          />
          <Route
            path="blueprints"
            element={
              <RequireAuth permission="bots:write">
                <Suspense fallback={<RouteFallback />}>
                  <BlueprintsPage />
                </Suspense>
              </RequireAuth>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={<RouteFallback />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="tenants"
            element={
              <RequireAuth permission="tenants:read">
                <Suspense fallback={<RouteFallback />}>
                  <TenantsPage />
                </Suspense>
              </RequireAuth>
            }
          />
          <Route
            path="audit-log"
            element={
              <RequireAuth permission="platform:admin">
                <Suspense fallback={<RouteFallback />}>
                  <AuditLogPage />
                </Suspense>
              </RequireAuth>
            }
          />

          {/* Any authenticated path that matched none of the above — a stale
              link, a typo — renders inside the shell (sidebar/topbar intact)
              rather than silently bouncing to Overview. An unauthenticated
              visit never reaches this: RequireAuth on the parent route
              redirects to /login first. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
